/**
 * photoQuality.js — client-side pre-scan quality check.
 *
 * Runs entirely in the browser (no upload) to catch photos that would
 * frustrate the AI: too dark, too bright, blurry, or low-resolution.
 * Cheap heuristics — ~150-400ms per photo on a typical phone.
 *
 * Returns { level, issues[], tips[] }:
 *   level: "ok" | "warn" | "poor"
 *   issues[]: list of short codes ("dark", "bright", "blurry", "low_res", "flat")
 *   tips[]: user-facing advice tailored to the issues found
 *
 * The recipes-scan pipeline still uploads whatever the user provides;
 * this hook only gives them a heads-up + option to retake. Never blocks.
 */

// Thresholds tuned against real cookbook photos taken with iPhone 12–15
// and mid-range Androids in typical kitchen lighting.
const DARK_THRESHOLD = 60;      // 0-255 mean luminance
const BRIGHT_THRESHOLD = 215;
const FLAT_STD_DEV = 18;         // low contrast (washed out)
const BLUR_LAPLACIAN = 55;       // variance of laplacian — lower = blurrier
const LOW_RES_MIN = 800;         // shortest side in pixels

function _loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
    img.src = url;
  });
}

/**
 * Analyse a File (image) for scan-usability issues.
 * Downscales to 400px on the longest side before analysing so it's fast
 * even on a 12MP phone shot.
 */
export async function analysePhoto(file) {
  if (!file || !file.type?.startsWith("image/")) {
    return { level: "ok", issues: [], tips: [], analysed: false };
  }
  let img;
  try { img = await _loadImage(file); }
  catch { return { level: "ok", issues: [], tips: [], analysed: false }; }

  // Downscale for speed. 400px longest side keeps the analysis under
  // ~200ms on a modest phone while preserving enough detail for the
  // blur/brightness estimators to be meaningful.
  const scale = 400 / Math.max(img.naturalWidth, img.naturalHeight);
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return { level: "ok", issues: [], tips: [], analysed: false };
  ctx.drawImage(img, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  // --- Luminance stats (Rec. 709) ---
  let sum = 0, sumSq = 0;
  const lumArr = new Float32Array(w * h);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    // Standard perceptual luminance weights
    const lum = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
    lumArr[j] = lum;
    sum += lum;
    sumSq += lum * lum;
  }
  const n = w * h;
  const mean = sum / n;
  const variance = sumSq / n - mean * mean;
  const stdDev = Math.sqrt(Math.max(0, variance));

  // --- Blur estimation: variance of 3×3 Laplacian ---
  // High-frequency content proxy. Sharp photos → high variance; blurred → low.
  let lapSum = 0, lapSumSq = 0, lapN = 0;
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const c = lumArr[y * w + x];
      const t = lumArr[(y - 1) * w + x];
      const b = lumArr[(y + 1) * w + x];
      const l = lumArr[y * w + x - 1];
      const r = lumArr[y * w + x + 1];
      const lap = 4 * c - t - b - l - r;
      lapSum += lap;
      lapSumSq += lap * lap;
      lapN++;
    }
  }
  const lapMean = lapSum / lapN;
  const lapVar = lapSumSq / lapN - lapMean * lapMean;

  // --- Verdict ---
  const issues = [];
  const tips = [];
  if (mean < DARK_THRESHOLD) {
    issues.push("dark");
    tips.push("Move to a brighter spot — a window or a lamp above the page helps a lot.");
  } else if (mean > BRIGHT_THRESHOLD) {
    issues.push("bright");
    tips.push("Photo is over-exposed — avoid direct sunlight or bright overhead lights on the page.");
  }
  if (stdDev < FLAT_STD_DEV && !issues.includes("dark") && !issues.includes("bright")) {
    issues.push("flat");
    tips.push("Photo looks a bit washed out — angle the phone so the page has clear contrast between text and paper.");
  }
  if (lapVar < BLUR_LAPLACIAN) {
    issues.push("blurry");
    tips.push("Photo may be blurry — tap the screen where the text is to focus, hold steady, and try again.");
  }
  if (Math.min(img.naturalWidth, img.naturalHeight) < LOW_RES_MIN) {
    issues.push("low_res");
    tips.push("Photo resolution is low — take a fresh shot with your camera rather than using a screenshot or downloaded image.");
  }

  // Two "warn"-level issues stack up to "poor" so the UI can escalate.
  let level = "ok";
  if (issues.includes("blurry") || issues.includes("dark") || issues.includes("bright")) level = "poor";
  else if (issues.length > 0) level = "warn";

  return {
    level,
    issues,
    tips,
    analysed: true,
    metrics: { mean, stdDev, lapVar, width: img.naturalWidth, height: img.naturalHeight },
  };
}

/**
 * autoFixImage(file) — one-tap brightness & contrast rescue for a scanned photo.
 *
 * Applies a histogram stretch (2nd → 98th percentile) plus a gentle midtone
 * gamma correction targeted at a mean luminance of ~140/255. This turns a
 * dark, washed-out or over-exposed page shot into something the OCR / LLM
 * can actually read, WITHOUT the risks of an auto-crop (false edge detection
 * on textured tablecloths etc.).
 *
 * Deliberately does NOT crop — the caller can rerun analysePhoto() after
 * the fix to update the quality badge.
 *
 * Returns { file, dataUrl, applied: { blackPoint, whitePoint, gamma } } or null
 * if the file couldn't be processed. Output is JPEG at 0.88 quality — good
 * for OCR while keeping upload size sensible.
 */
export async function autoFixImage(file) {
  if (!file || !file.type?.startsWith("image/")) return null;
  let img;
  try { img = await _loadImage(file); }
  catch { return null; }

  // Keep full resolution so the AI still gets the sharpest text possible.
  // Only cap absurdly-large phone shots (>4000px) to keep memory sane on
  // low-end Android — most modern phones ship ~3000-4000px.
  const maxDim = 4000;
  const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  const imgData = ctx.getImageData(0, 0, w, h);
  const data = imgData.data;

  // --- Build a 256-bin luminance histogram ---
  const hist = new Uint32Array(256);
  const n = w * h;
  for (let i = 0; i < data.length; i += 4) {
    const lum = (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) | 0;
    hist[Math.min(255, Math.max(0, lum))]++;
  }

  // --- Find 2nd and 98th percentile luminance for a clip-limited stretch.
  // Percentile rather than min/max avoids single hot pixels ruining the range.
  const lowCount = Math.floor(n * 0.02);
  const highCount = Math.floor(n * 0.98);
  let blackPoint = 0, whitePoint = 255, cum = 0;
  for (let i = 0; i < 256; i++) {
    cum += hist[i];
    if (cum >= lowCount) { blackPoint = i; break; }
  }
  cum = 0;
  for (let i = 0; i < 256; i++) {
    cum += hist[i];
    if (cum >= highCount) { whitePoint = i; break; }
  }
  // Guard: if percentiles collapsed (nearly-uniform image), fall back to a
  // safe minimal stretch so we don't divide by ~0.
  if (whitePoint - blackPoint < 20) {
    blackPoint = Math.max(0, blackPoint - 10);
    whitePoint = Math.min(255, whitePoint + 10);
  }
  const stretchRange = whitePoint - blackPoint;

  // --- Gamma nudged toward a target midtone (mean ~140).
  // Estimate current mean from the histogram, then pick gamma so that
  // stretched midtone hits target. gamma < 1 brightens, > 1 darkens.
  let meanSum = 0;
  for (let i = 0; i < 256; i++) meanSum += i * hist[i];
  const currentMean = meanSum / n;
  const targetMean = 140;
  // After a linear stretch, current mean maps to (mean - black)/range.
  // We want that ^ (1/gamma) ≈ target/255 → gamma = log(m')/log(t').
  const stretchedMean = Math.min(0.999, Math.max(0.001, (currentMean - blackPoint) / stretchRange));
  const targetNorm = targetMean / 255;
  let gamma = Math.log(stretchedMean) / Math.log(targetNorm);
  // Clamp gamma so we never crunch the image into nonsense.
  gamma = Math.max(0.5, Math.min(2.2, gamma));

  // --- Build a 256-entry LUT for speed (one pow() per bin, not per pixel).
  const lut = new Uint8ClampedArray(256);
  for (let i = 0; i < 256; i++) {
    const stretched = (i - blackPoint) / stretchRange;
    const clamped = Math.min(1, Math.max(0, stretched));
    const gammaCorrected = Math.pow(clamped, 1 / gamma);
    lut[i] = Math.round(gammaCorrected * 255);
  }

  // --- Apply LUT to each channel. Handle R, G, B independently to keep
  // hue neutral (per-channel stretch would shift the white balance).
  // The LUT is built from luminance, so we apply it to each channel — this
  // is the simplest way that keeps colour cast intact.
  for (let i = 0; i < data.length; i += 4) {
    data[i]     = lut[data[i]];
    data[i + 1] = lut[data[i + 1]];
    data[i + 2] = lut[data[i + 2]];
    // alpha untouched
  }
  ctx.putImageData(imgData, 0, 0);

  // --- Export as JPEG. 0.88 quality is a well-known sweet spot between
  // file size and OCR fidelity. Preserve the original file name/stem so
  // the review screen still shows something meaningful.
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.88));
  if (!blob) return null;
  const stem = (file.name || "photo").replace(/\.[^.]+$/, "");
  const fixedFile = new File([blob], `${stem}-autofixed.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  return {
    file: fixedFile,
    dataUrl: canvas.toDataURL("image/jpeg", 0.88),
    applied: { blackPoint, whitePoint, gamma: Math.round(gamma * 100) / 100 },
  };
}
